import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useQueryParams } from 'use-query-params'
import _ from 'lodash'
import { CategoryListQueryResult, useCategoryListQuery } from '@graphql/queries/__generated__/category.generated'
import { CartDocument, CartLinesQueryResult, useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { CartTypeEnum } from '../../../../generated/gqlTypes'
import { useCartUpdateProductVariantMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import {
  useCartClearMutation,
  useCartLinesUpdateBySupplyMutation,
} from '@graphql/mutations/__generated__/cart.generated'
import { useAddFavoritesProductMutation } from '@graphql/mutations/__generated__/me.generated'
import Loader from '@components/Loader'
import CartLinesCategory from './CartLinesCategory'
import { CartParams } from '../types'
import { DataLayerItem, googlePushProductRemoveFromCart } from '../../../utils/dataLayer'
import { HOME_URL } from '../../../urls'
import { mutationWarningsToMessages } from '../../../misc/utils'
import useGlobalLoader from '../../../hooks/useGlobalLoader'
import { Link } from 'react-router-dom'
import Button from '@mui/material/Button'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'

type CartLineNode = CartLinesQueryResult['data']['cartLines']['results'][0]

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface Props {
  isAuthenticated: boolean
  lineLoading: number | null

  setLineLoading(pk: number | null): void

  onClickProduct(specId: string | number, slug: string): void
}

const CartLinesContainer: React.FC<Props> = ({ isAuthenticated, lineLoading, setLineLoading, onClickProduct }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [, setParams] = useQueryParams(CartParams)
  const { setLoading } = useGlobalLoader()

  const categoryListQueryResult = useCategoryListQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
  })

  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
      },
      'CartShippingDiscounts',
    ],
  })
  const [cartClear] = useCartClearMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
      },
    ],
  })
  const [addToFavorites] = useAddFavoritesProductMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['MeFavoritesProducts', 'MeFavoritesCategoriesInfo'],
  })
  const [cartLinesUpdateBySupply] = useCartLinesUpdateBySupplyMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType: CartTypeEnum.Default, includeLinesByCategory: true },
      },
    ],
  })

  const handleEditQty = async (line: CartLineNode, qty: number, options?: { silent?: boolean }) => {
    setLineLoading(line.pk)
    try {
      const variant = line.variant
      const response = await cartUpdateVariant({
        variables: {
          variant: variant.pk,
          cartLine: line.pk,
          quantity: qty,
          cartType: CartTypeEnum.Default,
        },
      })
      if (response.data?.cartUpdateVariant.errors?.length) {
        throw Error(response.data.cartUpdateVariant.errors[0].messages[0])
      }
      const price = !line.supplyParts.length
        ? line.unitPrice.discount
        : _.sumBy(line.supplyParts, part => part.unitPrice.discount * part.quantity)
      const items: DataLayerItem[] = [
        {
          item_id: variant.sku,
          item_name: line.variant.product.siteSpecifics[0]?.name,
          index: 0,
          item_variant: variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`).join(),
          quantity: line.quantity,
          price,
        },
      ]
      googlePushProductRemoveFromCart({ currency: line.unitPrice.currency, value: price * line.quantity, items })
      if (!options?.silent) {
        enqueueSnackbar(
          t('snackbarMessages.success.shoppingBagModified', 'Your shopping bag has been modified successfully!'),
          {
            variant: 'success',
          }
        )
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const handleRemoveLine = async (line: CartLineNode, options?: { silent?: boolean }) => {
    await handleEditQty(line, 0, options)
  }

  const handleSaveForLaterLine = async (productPk: number, line: CartLineNode) => {
    setLineLoading(line.pk)
    try {
      await addToFavorites({
        variables: {
          pk: productPk,
        },
      })
      await handleRemoveLine(line, { silent: true })
      enqueueSnackbar(t('snackbarMessages.success.productSavedForLater', 'Product has been saved for later.'), {
        variant: 'success',
      })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const handleEditLine = async (linePk: number) => {
    setParams({ editLinePk: linePk }, 'replaceIn')
  }

  const handleApplySupplyToAll = async (supplyLineId: string) => {
    setLoading(true)
    try {
      const response = await cartLinesUpdateBySupply({
        variables: { cartType: CartTypeEnum.Default, supplyLineId },
      })
      mutationWarningsToMessages(response.data?.cartLinesUpdateBySupply.warnings).forEach(message =>
        enqueueSnackbar(message, { variant: 'warning' })
      )
      mutationWarningsToMessages(response.data?.cartLinesUpdateBySupply.errors).forEach(message =>
        enqueueSnackbar(message, { variant: 'error' })
      )
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLoading(false)
  }

  const handleClearCategory = async (categoryId: string) => {
    setLineLoading(-1)
    try {
      await cartClear({ variables: { cartType: CartTypeEnum.Default, category: categoryId } })
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setLineLoading(null)
  }

  const categories = categoryListQueryResult.data?.categories || []
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  const sortedCategories: CategoryListQueryResult['data']['categories'] = categories.filter(cat =>
    Object.keys(categoriesWithLines).includes(cat.pk.toString())
  )

  return (
    <div>
      {!sortedCategories.length && (
        <div className='bg-white shadow-card rounded-lg relative'>
          <div className='py-96 px-20 text-center relative flex flex-col items-center'>
            <Loader
              className='spinner spinner--small'
              type='ThreeDots'
              color='#333'
              visible={cartQueryResult.loading}
              height={100}
              width={100}
            />
            {!cartQueryResult.loading && (
              <>
                <ShoppingBagOutlinedIcon className='text-gray-300 mb-20' sx={{ fontSize: 80 }} />
                <span className='text-xl font-bold'>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>
                <span className='my-10 text-13 text-gray-700'>
                  {t('description.shoppingBagIsEmpty', 'Looks like you haven’t added anything yet.')}
                </span>
                <Button
                  component={Link}
                  to={HOME_URL}
                  variant='contained'
                  color='secondary'
                  disableElevation
                  disableRipple
                  size='large'
                  className='mt-30 px-40'
                >
                  <span className='font-bold'>{t('button.continueShopping', 'Continue Shopping')}</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {sortedCategories.map((cat, idx) => (
        <CartLinesCategory
          key={cat.pk}
          category={cat}
          lineLoading={lineLoading}
          isAuthenticated={isAuthenticated}
          onClickProduct={onClickProduct}
          handleEditLine={handleEditLine}
          handleRemoveLine={handleRemoveLine}
          handleEditQty={handleEditQty}
          handleSaveForLaterLine={handleSaveForLaterLine}
          handleApplySupplyToAll={cat.level === 0 ? handleApplySupplyToAll : undefined}
          initialize={idx === 0}
          cartClear={handleClearCategory}
          categoryLinesInfo={categoriesWithLines[cat.pk] || []}
        />
      ))}
    </div>
  )
}

export default CartLinesContainer
